import React, { useState } from 'react';
import { Switch } from '@mantine/core';
import { useConsent } from '@/infrastructure/consent/consentManager';
import useIntl from '@/infrastructure/hooks/useIntl';
import styles from './index.module.css';

export const ConsentDialog: React.FC = () => {
  const t = useIntl('consent');
  const {
    categories,
    isDialogOpen,
    hasConsented,
    acceptAll,
    rejectAll,
    updateCategories,
    closeConsentSettings
  } = useConsent();

  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [analyticsState, setAnalyticsState] = useState<boolean>(categories.analytics);
  const [perfState, setPerfState] = useState<boolean>(categories.performanceDiagnostics);

  // Sync internal state when categories change
  React.useEffect(() => {
    setAnalyticsState(categories.analytics);
    setPerfState(categories.performanceDiagnostics);
  }, [categories]);

  if (!isDialogOpen) return null;

  const handleSavePreferences = () => {
    updateCategories({
      analytics: analyticsState,
      performanceDiagnostics: perfState
    });
  };

  return (
    <div className={styles.overlay} onClick={hasConsented ? closeConsentSettings : undefined}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('title')}</h2>
          {hasConsented && (
            <button
              className={styles.closeButton}
              onClick={closeConsentSettings}
              aria-label="Close modal"
            >
              ✕
            </button>
          )}
        </div>

        <div className={styles.content}>
          <p className={styles.description}>
            {t('description')}
          </p>

          {showDetails && (
            <div className={styles.categoriesContainer}>
              {/* Category 1: Strictly Necessary */}
              <div className={styles.categoryCard}>
                <div className={styles.categoryHeader}>
                  <span className={styles.categoryTitle}>{t('strictlyNecessaryTitle')}</span>
                  <span className={styles.categoryBadge}>{t('strictlyNecessaryBadge')}</span>
                </div>
                <p className={styles.categoryDescription}>
                  {t('strictlyNecessaryDescription')}
                </p>
              </div>

              {/* Category 2: Analytics */}
              <div className={styles.categoryCard}>
                <div className={styles.categoryHeader}>
                  <span className={styles.categoryTitle}>{t('analyticsTitle')}</span>
                  <Switch
                    checked={analyticsState}
                    onChange={e => setAnalyticsState(e.currentTarget.checked)}
                    color="violet"
                    size="md"
                    aria-label="Toggle Analytics"
                  />
                </div>
                <p className={styles.categoryDescription}>
                  {t('analyticsDescription')}
                </p>
              </div>

              {/* Category 3: Performance & Diagnostics */}
              <div className={styles.categoryCard}>
                <div className={styles.categoryHeader}>
                  <span className={styles.categoryTitle}>{t('performanceTitle')}</span>
                  <Switch
                    checked={perfState}
                    onChange={e => setPerfState(e.currentTarget.checked)}
                    color="violet"
                    size="md"
                    aria-label="Toggle Performance and Diagnostics"
                  />
                </div>
                <p className={styles.categoryDescription}>
                  {t('performanceDescription')}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={handleSavePreferences}
            style={{ width: '100%' }}
          >
            {t('saveCustom')}
          </button>

          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={rejectAll}
            >
              {t('rejectOptional')}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={acceptAll}
            >
              {t('acceptAll')}
            </button>
          </div>

          <button
            type="button"
            className={styles.toggleDetailsBtn}
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? t('hideDetails') : t('customizePreferences')}
          </button>
        </div>
      </div>
    </div>
  );
};
